/**
 * Map-side Story HUD for Pathfinder (fullscreen-friendly).
 *
 * One card over the map with Story Mode's timeline and an AI summary. The AI
 * summary can be general or focused on whatever the analyst asks for
 * ("how many cameras did they pass", "interactions with co-travelers"), and it
 * is given every dataset loaded on the map (track, dwells, cameras passed,
 * co-travelers, devices, itineraries, RF signals, uploaded files).
 *
 * The card drags, minimizes to a chip, docks as a strip along the bottom of
 * the map, resizes in height, exports (text / md / PNG / PPTX), and shares
 * the HUD style (theme / accent / opacity / text size) with the legend.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputBase,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Modal,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AutoAwesome as AiIcon,
  AutoStories as StoryIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  CropFree as CropIcon,
  ViewQuilt as FormatIcon,
  DirectionsCar as TravelIcon,
  Home as DwellIcon,
  Numbers as MapLabelsIcon,
  Remove as MinimizeIcon,
  Refresh as RefreshIcon,
  VerticalAlignBottom as EndIcon,
  Videocam as CameraIcon,
} from '@mui/icons-material';
import {
  generateStoryAiSummary,
  STORY_DOCK_HEIGHT_MIN,
  STORY_FLOAT_HEIGHT_MIN,
  STORY_TIMELINE_FORMATS,
  inView,
  useNarrativeStore,
  useVisibleStoryEvents,
  type MapViewBounds,
  type StoryAiDetail,
  type StoryEvent,
} from '../../stores/narrativeStore';
import { usePathfinderStore } from '../../stores/pathfinderStore';
import { routeSequenceEntryColor } from '@signals/app-shell/services/routeSequence';
import type { CoTravelerResult } from '../../services/pathfinderApi';
import {
  HudChip,
  HudDragGlyph,
  HudExportButton,
  HudLayoutButton,
  HudStyleButton,
  HudTextSizeButtons,
  hudPortalContainer,
  useHudDrag,
  useHudStyle,
  type HudStyleInfo,
} from './PathfinderHudKit';
import { buildStoryAiDoc, buildStoryDoc, type HudExportDoc } from './pathfinderHudExport';
import { parseAiSummary } from './storyAiFormat';
import type { SentinelEngineInput } from './PathfinderSentinel';

/** Datasets loaded on the map, handed to the AI alongside the timeline. */
export interface StoryMapDatasets extends SentinelEngineInput {
  coTravelerResults?: CoTravelerResult[];
  /** RF signal payload ({ wifi: [], bluetooth: [], cellular: [] }) when the RF layer is loaded. */
  signalData?: any;
}

export interface PathfinderMapNarrativeOverlayProps extends StoryMapDatasets {
  currentTime: Date | null;
  isFullExtent?: boolean;
  /** Fly the map to an event and move the time slider to its start. */
  onEventSelect?: (event: StoryEvent) => void;
}

const Z_STORY = 1150;
const Z_AI = 1151;
const STORY_DEFAULT = { top: 12, left: 12 };
const AI_DEFAULT = { top: 12, right: 72 };
/** Docked AI card = a column at the right of the bottom strip; the docked Story strip gives way to it. */
const AI_DOCK_WIDTH = 'min(480px, 42%)';
const AI_DOCK_GAP = 12;
interface DockGeometry {
  bottom: number;
  height: number;
  left?: number | string;
  right?: number | string;
  width?: number | string;
}
const dockSx = (d: DockGeometry) => ({
  left: d.left ?? 12,
  right: d.right ?? 12,
  bottom: d.bottom,
  top: 'auto',
  width: d.width ?? 'auto',
  maxWidth: 'none',
  height: d.height,
  maxHeight: 'calc(100% - 24px)',
});
/** Zig-zag timeline geometry (docked Story). */
const ZZ_SLOT_W = 130; // minimum horizontal distance between consecutive markers (spreads to fill the strip)
const ZZ_CARD_W = 225; // event card width (overlaps the next slot, which sits on the other side)
const ZZ_DIAG = 16; // 45° connector run/rise

const formatDuration = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m`;
  return `${Math.max(0, Math.round(seconds))}s`;
};

const formatMiles = (km: number): string => {
  const miles = km * 0.621371;
  if (miles < 0.1) return `${Math.round(miles * 5280)} ft`;
  return `${miles.toFixed(2)} mi`;
};

const fmtTime = (d: Date) => d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
const fmtDay = (d: Date) => d.toLocaleDateString([], { month: 'short', day: 'numeric' });
const fmtCoord = (c?: [number, number]) => (c ? `${c[0].toFixed(5)}, ${c[1].toFixed(5)}` : undefined);
const eventSecs = (e: StoryEvent) => (e.endTime.getTime() - e.startTime.getTime()) / 1000;
const eventNo = (e: StoryEvent, idx: number) => e.seq ?? idx + 1;
const kindName = (e: StoryEvent) => (e.type === 'dwell' ? 'Dwelling' : e.type === 'camera' ? 'Camera' : 'Traveling');
const cameraColor = (e: StoryEvent) => routeSequenceEntryColor({ kind: 'camera', category: e.category ?? undefined });
const fmtMeters = (m?: number) => (m === undefined ? '' : m < 1000 ? `${Math.round(m)} m` : `${(m / 1000).toFixed(1)} km`);
/** Average speed for a travel segment, mph. */
const avgMph = (e: StoryEvent): number | null => {
  const s = eventSecs(e);
  if (e.type !== 'travel' || !e.distance || s <= 0) return null;
  return (e.distance * 0.621371) / (s / 3600);
};
/** Ledger geometry. */
const LEDGER_LABEL_W = 64;
const LEDGER_COL_MIN = 150;
/** Stops & legs geometry. */
const STOP_TILE_W = 168;
const CAM_TILE_W = 118;
const CAM_DOT_W = 58;
const LEG_W = 104;
const dwellPlace = (e: StoryEvent) => {
  const a = (e.address || '').trim();
  if (a && !/^-?\d+\.\d+,\s*-?\d+\.\d+$/.test(a)) return a;
  return fmtCoord(e.location) || '';
};

/* ------------------------------------------------------------------------ */
/* AI context: everything loaded on the map, compacted for the model        */
/* ------------------------------------------------------------------------ */

const iso = (d: any): string | undefined => {
  const t = d instanceof Date ? d : d ? new Date(d) : null;
  return t && !Number.isNaN(t.getTime()) ? t.toISOString() : undefined;
};

/**
 * Build the `context` block for the Story AI call from the datasets the page
 * has loaded. Cameras passed are added by the store (published by the map).
 * Lists are capped so the prompt stays bounded. When `bounds` is given,
 * anything with a position (points, dwells, co-traveler locations) is cropped
 * to the map view so the model only sees what the analyst is looking at.
 */
export function buildStoryAiContext(d: StoryMapDatasets, entities: string[], bounds: MapViewBounds | null = null): Record<string, any> {
  const ctx: Record<string, any> = {};
  const within = (lat: unknown, lng: unknown) => {
    if (!bounds) return true;
    const la = Number(lat);
    const ln = Number(lng);
    return Number.isFinite(la) && Number.isFinite(ln) && inView(bounds, [la, ln]);
  };
  const allPoints = d.dataPoints ?? [];
  const points = bounds ? allPoints.filter((p) => Array.isArray(p.coordinates) && within(p.coordinates[0], p.coordinates[1])) : allPoints;
  if (bounds) {
    ctx.view = {
      note: 'Everything below is cropped to the current map view (only features inside the view are counted or listed).',
      bounds,
      trackPointsInView: points.length,
      trackPointsTotal: allPoints.length,
    };
  }
  if (points.length > 0) {
    let t0 = Infinity;
    let t1 = -Infinity;
    const devices = new Set<string>();
    const files = new Set<string>();
    for (const p of points) {
      const t = p.timestamp instanceof Date ? p.timestamp.getTime() : new Date(p.timestamp as any).getTime();
      if (Number.isFinite(t)) {
        if (t < t0) t0 = t;
        if (t > t1) t1 = t;
      }
      devices.add(`${p.studentId || p.deviceId}${p.deviceType ? ` (${p.deviceType})` : ''}`);
      const f = (p as any).metadata?.originalFileName;
      if (f) files.add(String(f));
    }
    ctx.track = {
      entities,
      pointCount: points.length,
      devices: Array.from(devices).slice(0, 30),
      start: Number.isFinite(t0) ? new Date(t0).toISOString() : undefined,
      end: Number.isFinite(t1) ? new Date(t1).toISOString() : undefined,
    };
    if (files.size) ctx.uploadedFiles = Array.from(files);
  }
  if ((d.routes ?? []).length) {
    ctx.routes = { count: d.routes.length };
  }
  // actualDevices: entity id -> devices for that entity.
  const devs: any[] = [];
  for (const [entity, list] of Object.entries(d.actualDevices ?? {})) {
    for (const x of (list ?? []) as any[]) {
      devs.push({
        entity,
        id: x.id ?? x.deviceId ?? x.device_id,
        type: x.type ?? x.deviceType ?? x.device_type,
        name: x.name,
      });
    }
  }
  if (devs.length) ctx.devices = devs.slice(0, 40);
  const allDwells = (d.dwellLocations ?? []) as any[];
  const dwells = bounds ? allDwells.filter((x) => within(x.latitude, x.longitude)) : allDwells;
  if (dwells.length) {
    const sorted = [...dwells].sort((a, b) => Number(b.duration_seconds || 0) - Number(a.duration_seconds || 0));
    ctx.dwells = {
      count: dwells.length,
      ...(bounds && allDwells.length !== dwells.length ? { total: allDwells.length } : {}),
      top: sorted.slice(0, 20).map((x) => ({
        entity: x.entity_id,
        device: x.device_type,
        lat: x.latitude,
        lng: x.longitude,
        place: x.place_name,
        address: x.place_address,
        start: iso(x.start_time),
        end: iso(x.end_time),
        durationS: Number(x.duration_seconds || 0),
      })),
    };
  }
  const allCot = d.coTravelerResults ?? [];
  // Co-travelers: keep the ones with a known location in view; results with no
  // locations at all cannot be placed, so they stay.
  const cot = bounds
    ? allCot.filter((r) => {
        const locs = r.frequentLocations ?? [];
        return locs.length === 0 || locs.some((l) => within(l.location?.[0], l.location?.[1]));
      })
    : allCot;
  if (cot.length) {
    ctx.coTravelers = {
      count: cot.length,
      ...(bounds && allCot.length !== cot.length ? { total: allCot.length } : {}),
      items: cot.slice(0, 25).map((r) => ({
        primary: r.primaryDevice,
        device: r.coTravelerDevice,
        entityType: r.coTravelerEntityType,
        encounters: r.totalEncounters,
        totalDurationS: r.totalDuration,
        avgProximityM: r.averageProximity,
        confidence: r.confidenceScore,
        temporalOverlapPct: r.temporalOverlap,
        spatialOverlapPct: r.spatialOverlap,
        firstEncounter: iso((r.encounters?.[0] as any)?.startTime ?? (r.encounters?.[0] as any)?.timestamp),
        lastEncounter: iso(
          (r.encounters?.[r.encounters.length - 1] as any)?.endTime ??
            (r.encounters?.[r.encounters.length - 1] as any)?.timestamp,
        ),
        frequentLocations: (r.frequentLocations ?? []).slice(0, 3).map((l) => ({
          name: l.name,
          lat: l.location?.[0],
          lng: l.location?.[1],
          encounters: l.encounters,
          totalDurationS: l.totalDuration,
        })),
      })),
    };
  }
  const itin = (d.itinerarySubmissions ?? []) as any[];
  if (itin.length) {
    ctx.itineraries = {
      count: itin.length,
      items: itin.slice(0, 10).map((s) => ({
        entity: s.student_number,
        runAt: s.route_run_datetime,
        entries: (s.route_entries ?? []).slice(0, 25),
      })),
    };
  }
  const rf = d.signalData;
  if (rf && (rf.wifi?.length || rf.bluetooth?.length || rf.cellular?.length)) {
    ctx.rfSignals = {
      wifi: rf.wifi?.length ?? 0,
      bluetooth: rf.bluetooth?.length ?? 0,
      cellular: rf.cellular?.length ?? 0,
      sample: [
        ...(rf.wifi ?? []).slice(0, 8).map((w: any) => ({ kind: 'wifi', id: w.ssid || w.bssid, seen: w.observation_count })),
        ...(rf.bluetooth ?? []).slice(0, 8).map((b: any) => ({ kind: 'bluetooth', id: b.name || b.address, seen: b.observation_count })),
        ...(rf.cellular ?? []).slice(0, 5).map((c: any) => ({ kind: 'cellular', id: c.operator || c.cell_tower_id, seen: c.observation_count })),
      ],
    };
  }
  return ctx;
}

/** One-line inventory shown in the AI popover so the analyst knows what the model sees. */
function describeDatasets(
  ctx: Record<string, any>,
  eventCount: number,
  cameras: { loadedCount: number; passedCount: number; nearCount?: number; radiusM?: number; passed?: Array<{ lat: number; lng: number }> } | null,
  bounds: MapViewBounds | null = null,
): string[] {
  const parts: string[] = [`${eventCount} events`];
  if (ctx.track) parts.push(`${Number(ctx.track.pointCount).toLocaleString()} pts`);
  if (ctx.routes) parts.push(`${ctx.routes.count} routes`);
  if (ctx.dwells) parts.push(`${ctx.dwells.count} dwells`);
  if (cameras) {
    const passed = bounds && cameras.passed ? cameras.passed.filter((p) => inView(bounds, [p.lat, p.lng])).length : cameras.passedCount;
    parts.push(`${passed} cameras passed`);
    if (cameras.loadedCount && !bounds) parts.push(`${cameras.loadedCount.toLocaleString()} cameras`);
  }
  if (ctx.coTravelers) parts.push(`${ctx.coTravelers.count} co-travelers`);
  if (ctx.itineraries) parts.push(`${ctx.itineraries.count} itineraries`);
  if (ctx.rfSignals) parts.push(`${ctx.rfSignals.wifi + ctx.rfSignals.bluetooth + ctx.rfSignals.cellular} RF`);
  if (ctx.uploadedFiles) parts.push(`${ctx.uploadedFiles.length} file${ctx.uploadedFiles.length === 1 ? '' : 's'}`);
  if (bounds) parts.push('in map view');
  return parts;
}

const FOCUS_EXAMPLES: Array<{ label: string; focus: string }> = [
  { label: 'Cameras passed', focus: 'How many cameras did they pass, and where?' },
  { label: 'Co-travelers', focus: 'Describe interactions with co-travelers' },
  { label: 'Dwell routine', focus: 'Dwell routine and repeat visits' },
  { label: 'Anomalies', focus: 'Unusual patterns or anomalies' },
  { label: 'Phone vs vehicle', focus: 'Phone vs. vehicle separation' },
];

const AiSummaryBody: React.FC<{ text: string; hud: HudStyleInfo }> = ({ text, hud }) => {
  const { accent, fs } = hud;
  const blocks = useMemo(() => parseAiSummary(text), [text]);
  // Sections sit side by side - Summary | Key Findings | Conclusions - in as many
  // ~280px columns as fit (three across a full-width docked strip, one in a narrow card).
  const multi = blocks.length > 1;
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: multi ? 'repeat(auto-fit, minmax(min(280px, 100%), 1fr))' : '1fr',
        gap: 1.25,
        alignItems: 'start',
      }}
    >
      {blocks.map((block, i) => (
        <Box
          key={`${block.heading ?? 'body'}-${i}`}
          sx={{
            minWidth: 0,
            borderLeft: `3px solid ${accent}`,
            pl: 1.15,
            // Lead section (exec summary) keeps the full width; findings sit beside conclusions.
          }}
        >
          {block.heading && (
            <Typography
              sx={{
                fontSize: fs(0.62),
                fontWeight: 800,
                letterSpacing: 0.9,
                textTransform: 'uppercase',
                color: accent,
                fontFamily: 'inherit',
                mb: 0.45,
              }}
            >
              {block.heading}
            </Typography>
          )}
          {block.lines.map((line, j) =>
            line.kind === 'li' ? (
              <Typography
                key={j}
                sx={{
                  fontSize: fs(0.76),
                  lineHeight: 1.45,
                  fontFamily: 'inherit',
                  pl: 1.1,
                  position: 'relative',
                  '&::before': {
                    content: '"•"',
                    position: 'absolute',
                    left: 0,
                    color: accent,
                  },
                }}
              >
                {line.text}
              </Typography>
            ) : (
              <Typography key={j} sx={{ fontSize: fs(0.78), lineHeight: 1.5, fontFamily: 'inherit', mb: 0.6 }}>
                {line.text}
              </Typography>
            ),
          )}
        </Box>
      ))}
    </Box>
  );
};

/* ------------------------------------------------------------------------ */
/* Resize handle (top edge when docked, bottom edge when floating)           */
/* ------------------------------------------------------------------------ */

const ResizeHandle: React.FC<{
  edge: 'top' | 'bottom';
  color: string;
  onResize: (nextHeight: number) => void;
  getStartHeight: () => number;
  min: number;
}> = ({ edge, color, onResize, getStartHeight, min }) => {
  const ref = useRef<{ y: number; h: number; max: number } | null>(null);
  return (
    <Box
      onPointerDown={(e) => {
        if (e.button !== 0) return;
        const card = (e.currentTarget as HTMLElement).closest('[data-hud-card]') as HTMLElement | null;
        const parent = card?.offsetParent as HTMLElement | null;
        ref.current = { y: e.clientY, h: getStartHeight(), max: parent ? parent.clientHeight - 24 : 2000 };
        (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
        e.preventDefault();
        e.stopPropagation();
      }}
      onPointerMove={(e) => {
        const s = ref.current;
        if (!s) return;
        const dy = e.clientY - s.y;
        const next = edge === 'top' ? s.h - dy : s.h + dy;
        onResize(Math.round(Math.min(s.max, Math.max(min, next))));
      }}
      onPointerUp={(e) => {
        ref.current = null;
        (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
      }}
      onPointerCancel={() => {
        ref.current = null;
      }}
      sx={{
        position: 'absolute',
        left: 0,
        right: 0,
        [edge]: 0,
        height: 7,
        cursor: 'ns-resize',
        zIndex: 5,
        touchAction: 'none',
        '&::after': {
          content: '""',
          position: 'absolute',
          left: '50%',
          [edge]: 2,
          width: 36,
          height: 3,
          borderRadius: 2,
          bgcolor: color,
          opacity: 0.35,
          transform: 'translateX(-50%)',
          transition: 'opacity 0.15s',
        },
        '&:hover::after': { opacity: 0.9 },
      }}
    />
  );
};

/* ------------------------------------------------------------------------ */
/* AI summary dialog (centered glass overlay)                                */
/* ------------------------------------------------------------------------ */

const AiSummaryDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  hud: HudStyleInfo;
  inventory: string[];
  loading: boolean;
  initialFocus?: string | null;
  onGenerate: (focus: string | null, detail: StoryAiDetail) => void;
}> = ({ open, onClose, hud, inventory, loading, initialFocus, onGenerate }) => {
  const [focus, setFocus] = useState(initialFocus ?? '');
  const [detail, setDetail] = useState<StoryAiDetail>(hud.style.storyAiDetail);
  useEffect(() => {
    if (open) {
      setFocus(initialFocus ?? '');
      setDetail(hud.style.storyAiDetail);
    }
  }, [open, initialFocus, hud.style.storyAiDetail]);
  const { tokens, accent, fs } = hud;
  const go = (f: string | null) => {
    onGenerate(f, detail);
    onClose();
  };
  const glass = hud.bg.replace(/rgba\((\d+),\s*(\d+),\s*(\d+),\s*[\d.]+\)/, 'rgba($1, $2, $3, 0.62)');
  // Neutral "on" state for the short / detailed switch (grey, not the accent).
  const toggleOn = hud.style.theme === 'paper' ? 'rgba(0,0,0,0.10)' : 'rgba(255,255,255,0.14)';
  return (
    <Modal
      open={open}
      onClose={onClose}
      container={hudPortalContainer}
      closeAfterTransition
      BackdropProps={{
        sx: {
          background: 'radial-gradient(ellipse at center, rgba(0,0,0,0.28) 0%, rgba(0,0,0,0.55) 70%)',
          backdropFilter: 'blur(6px)',
        },
      }}
      sx={{ zIndex: 4000 }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          outline: 'none',
          width: 'min(480px, calc(100% - 40px))',
          maxHeight: 'calc(100% - 40px)',
        }}
      >
        <Box
          sx={{
            position: 'relative',
            overflow: 'auto',
            maxHeight: 'calc(100vh - 40px)',
            borderRadius: 3,
            bgcolor: glass,
            color: tokens.text,
            fontFamily: tokens.font,
            border: `1px solid ${accent}55`,
            boxShadow: `0 0 0 1px ${tokens.border}, 0 24px 64px rgba(0,0,0,0.45), 0 0 48px ${accent}22`,
            backdropFilter: `blur(${Math.max(hud.style.theme === 'paper' ? 12 : 18, tokens.blur)}px) saturate(1.2)`,
            px: 3,
            pt: 2.5,
            pb: 2.25,
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              pointerEvents: 'none',
              background: `radial-gradient(120% 80% at 50% -10%, ${accent}1a 0%, transparent 55%)`,
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 2,
              background: `linear-gradient(90deg, transparent, ${accent}aa, transparent)`,
            }}
          />

          <IconButton
            size="small"
            onClick={onClose}
            sx={{ position: 'absolute', top: 10, right: 10, color: tokens.muted, '&:hover': { color: tokens.text } }}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>

          <Box sx={{ position: 'relative', textAlign: 'center', mb: 2 }}>
            <Box
              sx={{
                width: 44,
                height: 44,
                mx: 'auto',
                mb: 1,
                borderRadius: '50%',
                display: 'grid',
                placeItems: 'center',
                bgcolor: `${accent}22`,
                border: `1px solid ${accent}66`,
                boxShadow: `0 0 24px ${accent}33`,
              }}
            >
              <AiIcon sx={{ fontSize: 22, color: accent }} />
            </Box>
            <Typography sx={{ fontSize: fs(1.15), fontWeight: 800, letterSpacing: 0.4, fontFamily: 'inherit', lineHeight: 1.2 }}>
              AI summary
            </Typography>
            <Typography sx={{ fontSize: fs(0.72), color: tokens.muted, fontFamily: 'inherit', mt: 0.4 }}>
              Summarize the timeline, or tell it what to focus on
            </Typography>
          </Box>

          {inventory.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 0.6, mb: 2 }}>
              {inventory.map((item) => (
                <Box
                  key={item}
                  sx={{
                    fontSize: fs(0.62),
                    px: 0.95,
                    py: 0.28,
                    borderRadius: 999,
                    border: `1px solid ${tokens.border}`,
                    bgcolor: 'rgba(255,255,255,0.04)',
                    color: tokens.muted,
                    fontFamily: 'inherit',
                    letterSpacing: 0.2,
                  }}
                >
                  {item}
                </Box>
              ))}
            </Box>
          )}

          <Box
            sx={{
              border: `1px solid ${tokens.border}`,
              bgcolor: 'rgba(0,0,0,0.22)',
              borderRadius: 2,
              px: 1.5,
              py: 1,
              transition: 'border-color 0.15s, box-shadow 0.15s',
              '&:focus-within': {
                borderColor: accent,
                boxShadow: `0 0 0 3px ${accent}33`,
              },
            }}
          >
            <Typography
              sx={{
                fontSize: fs(0.58),
                color: tokens.muted,
                letterSpacing: 1.1,
                textTransform: 'uppercase',
                fontFamily: 'inherit',
                mb: 0.4,
              }}
            >
              Focus
            </Typography>
            <InputBase
              fullWidth
              multiline
              minRows={2}
              maxRows={5}
              autoFocus
              placeholder="e.g. Summarize the subject’s travel and focus on how many cameras they passed…"
              value={focus}
              onChange={(e) => setFocus(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                  e.preventDefault();
                  go(focus.trim() || null);
                }
              }}
              sx={{
                fontSize: fs(0.88),
                color: tokens.text,
                fontFamily: 'inherit',
                '& textarea::placeholder': { color: tokens.muted, opacity: 0.8 },
              }}
            />
          </Box>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 0.55, mt: 1 }}>
            {FOCUS_EXAMPLES.map((p) => {
              const on = focus === p.focus;
              return (
                <Box
                  key={p.label}
                  role="button"
                  onClick={() => setFocus(p.focus)}
                  sx={{
                    fontSize: fs(0.64),
                    px: 0.9,
                    py: 0.3,
                    borderRadius: 999,
                    border: `1px solid ${on ? accent : tokens.border}`,
                    bgcolor: on ? `${accent}22` : 'rgba(255,255,255,0.03)',
                    color: on ? accent : tokens.text,
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                    whiteSpace: 'nowrap',
                    transition: 'all 0.15s',
                    '&:hover': { borderColor: accent, color: accent },
                  }}
                >
                  {p.label}
                </Box>
              );
            })}
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 0.5,
              mt: 1.75,
              p: 0.4,
              borderRadius: 2,
              bgcolor: 'rgba(0,0,0,0.22)',
              border: `1px solid ${tokens.border}`,
            }}
          >
            {([
              { id: 'short' as const, label: 'Concise', hint: 'A few sentences' },
              { id: 'detailed' as const, label: 'Detailed', hint: 'Full write-up' },
            ]).map((opt) => {
              const on = detail === opt.id;
              return (
                <Box
                  key={opt.id}
                  role="button"
                  onClick={() => setDetail(opt.id)}
                  sx={{
                    textAlign: 'center',
                    py: 0.7,
                    borderRadius: 1.5,
                    cursor: 'pointer',
                    bgcolor: on ? toggleOn : 'transparent',
                    color: on ? tokens.text : tokens.muted,
                    border: `1px solid ${on ? tokens.border : 'transparent'}`,
                    transition: 'all 0.15s',
                    '&:hover': { color: tokens.text },
                  }}
                >
                  <Typography sx={{ fontSize: fs(0.74), fontWeight: 800, fontFamily: 'inherit', lineHeight: 1.2 }}>
                    {opt.label}
                  </Typography>
                  <Typography sx={{ fontSize: fs(0.56), opacity: 0.75, fontFamily: 'inherit', lineHeight: 1.2 }}>
                    {opt.hint}
                  </Typography>
                </Box>
              );
            })}
          </Box>

          <Button
            fullWidth
            disabled={loading}
            startIcon={<AiIcon />}
            onClick={() => go(focus.trim() || null)}
            sx={{
              mt: 1.5,
              py: 1.15,
              bgcolor: `${accent}2e`,
              color: accent,
              border: `1px solid ${accent}66`,
              textTransform: 'none',
              fontWeight: 800,
              fontFamily: 'inherit',
              borderRadius: 2,
              boxShadow: `0 8px 24px ${accent}22`,
              '&:hover': { bgcolor: `${accent}44`, borderColor: accent },
              '&.Mui-disabled': { color: tokens.muted, bgcolor: 'transparent', borderColor: tokens.border },
            }}
          >
            {focus.trim()
              ? `Generate ${detail === 'short' ? 'short' : 'detailed'} focus`
              : `Generate ${detail === 'short' ? 'short' : 'detailed'} summary`}
          </Button>
          <Typography sx={{ fontSize: fs(0.58), color: tokens.muted, fontFamily: 'inherit', mt: 1, textAlign: 'center' }}>
            {focus.trim() ? 'Uses the focus above' : 'Leave focus blank for a general timeline'}
            {' · '}Ctrl+Enter
          </Typography>
        </Box>
      </Box>
    </Modal>
  );
};

/* ------------------------------------------------------------------------ */
/* Shared card chrome                                                        */
/* ------------------------------------------------------------------------ */

const CardHeader: React.FC<{
  hud: HudStyleInfo;
  panelId: 'story' | 'ai';
  icon: React.ReactNode;
  title: string;
  subtitle?: string;
  handleProps: Record<string, unknown>;
  docked: boolean;
  /** Content centred between the title and the controls (docked indicators). */
  center?: React.ReactNode;
  extra?: React.ReactNode;
  /** Export document builder; the card element is captured for PNG/PPTX. */
  getDoc: () => HudExportDoc;
  cardRef: React.RefObject<HTMLDivElement | null>;
  exportDisabled?: boolean;
  onMinimize: () => void;
  onClose: () => void;
}> = ({
  hud,
  panelId,
  icon,
  title,
  subtitle,
  handleProps,
  docked,
  center,
  extra,
  getDoc,
  cardRef,
  exportDisabled,
  onMinimize,
  onClose,
}) => {
  const btnSx = { color: hud.tokens.muted, p: 0.5, '&:hover': { color: hud.accent } };
  return (
    <Box
      {...(docked ? {} : handleProps)}
      sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1, pt: 0.75, pb: 0.5, userSelect: 'none' }}
    >
      {!docked && <HudDragGlyph color={hud.tokens.muted} />}
      <Box sx={{ display: 'flex', color: hud.accent }}>{icon}</Box>
      <Box sx={{ minWidth: 0, flex: center ? '0 1 auto' : 1 }}>
        <Typography noWrap sx={{ fontSize: hud.fs(0.82), fontWeight: 800, letterSpacing: 0.3, fontFamily: 'inherit', lineHeight: 1.2 }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography noWrap sx={{ fontSize: hud.fs(0.64), color: hud.tokens.muted, fontFamily: 'inherit', lineHeight: 1.2 }}>
            {subtitle}
          </Typography>
        )}
      </Box>
      {center && (
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', justifyContent: 'center', alignItems: 'center', px: 1, overflow: 'hidden' }}>
          {center}
        </Box>
      )}
      {extra}
      <Box data-hud-controls="" sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
        <HudTextSizeButtons sx={btnSx} />
        <HudExportButton sx={btnSx} getDoc={getDoc} cardRef={cardRef} solidBackground={hud.solidBg} accent={hud.accent} disabled={exportDisabled} />
        <HudLayoutButton id={panelId} sx={btnSx} />
        <HudStyleButton sx={btnSx} showStats />
        <Tooltip title="Minimize">
          <IconButton size="small" sx={btnSx} onClick={onMinimize}>
            <MinimizeIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Close">
          <IconButton size="small" sx={btnSx} onClick={onClose}>
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
};

const AccentStrip: React.FC<{ hud: HudStyleInfo }> = ({ hud }) => (
  <Box
    sx={{
      height: 3,
      flexShrink: 0,
      background: `linear-gradient(90deg, ${hud.accent}99 0%, ${hud.accent}80 55%, ${hud.accent}1a 100%)`,
    }}
  />
);

/** Shared card frame sx (float or dock). */
const cardFrameSx = (
  hud: HudStyleInfo,
  drag: ReturnType<typeof useHudDrag>,
  dock: DockGeometry | null,
  z: number,
  floatHeight: number,
) => ({
  ...(dock ? { position: 'absolute' as const, ...dockSx(dock) } : (drag.positionSx as object)),
  ...(hud.surfaceSx as object),
  zIndex: z,
  ...(dock
    ? {}
    : {
        width: hud.style.width,
        maxWidth: 'calc(100% - 80px)',
        // 0 = size to content; otherwise the user-dragged height.
        ...(floatHeight > 0 ? { height: floatHeight } : {}),
        maxHeight: 'calc(100% - 24px)',
      }),
  display: 'flex',
  flexDirection: 'column' as const,
  overflow: 'hidden',
});

/* ------------------------------------------------------------------------ */
/* Story card                                                                */
/* ------------------------------------------------------------------------ */

const StoryCard: React.FC<{
  currentTime: Date | null;
  isFullExtent: boolean;
  onEventSelect?: (event: StoryEvent) => void;
  /** Open the AI summary card (and the prompt when nothing has been generated yet). */
  onOpenAi: () => void;
}> = ({ currentTime, isFullExtent, onEventSelect, onOpenAi }) => {
  const hud = useHudStyle();
  const drag = useHudDrag('story', STORY_DEFAULT);
  const minimized = useNarrativeStore((s) => s.panelMinimized.story);
  const setMinimized = useNarrativeStore((s) => s.setPanelMinimized);
  const setPanelOpen = useNarrativeStore((s) => s.setPanelOpen);
  const layout = useNarrativeStore((s) => s.panelLayout.story);
  const allEvents = useNarrativeStore((s) => s.storyEvents);
  // Cropped to the map view when enabled; numbering follows this list.
  const events = useVisibleStoryEvents();
  const cropToView = useNarrativeStore((s) => s.storyCropToView);
  const setCropToView = useNarrativeStore((s) => s.setStoryCropToView);
  const showCameras = useNarrativeStore((s) => s.storyShowCameras);
  const setShowCameras = useNarrativeStore((s) => s.setStoryShowCameras);
  const cameraPassesAvailable = useNarrativeStore((s) => Boolean(s.storyCameras?.layerOn && s.storyCameras.passed.length > 0));
  const croppedOut = cropToView && events.length !== allEvents.length;
  const entities = useNarrativeStore((s) => s.storyEntities);
  const aiSummary = useNarrativeStore((s) => s.storyAiSummary);
  const aiFocus = useNarrativeStore((s) => s.storyAiFocus);
  const aiPanelOpen = useNarrativeStore((s) => s.panelsOpen.ai);
  const aiDocked = useNarrativeStore((s) => s.panelsOpen.ai && !s.panelMinimized.ai && s.panelLayout.ai === 'dock');
  const setActiveToolId = usePathfinderStore((s) => s.setActiveToolId);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const activeRef = useRef<HTMLDivElement | null>(null);
  const cardRef = useRef<HTMLDivElement | null>(null);
  const docked = layout === 'dock';
  // When the AI card is docked as well, the Story strip leaves room for it on the right.
  const dock: DockGeometry | null = docked
    ? { bottom: 12, height: hud.style.storyDockHeight, right: aiDocked ? `calc(${AI_DOCK_WIDTH} + ${AI_DOCK_GAP * 2}px)` : 12 }
    : null;
  const floatHeight = hud.style.storyFloatHeight;
  // Docked strip width, so slots can spread out to fill it when the
  // timeline is shorter than the card (otherwise it scrolls).
  const [railWidth, setRailWidth] = useState(0);
  const railObserver = useRef<ResizeObserver | null>(null);
  const railRef = (el: HTMLDivElement | null) => {
    railObserver.current?.disconnect();
    railObserver.current = null;
    if (!el) return;
    setRailWidth(el.clientWidth);
    if (typeof ResizeObserver !== 'undefined') {
      railObserver.current = new ResizeObserver(() => setRailWidth(el.clientWidth));
      railObserver.current.observe(el);
    }
  };
  const slotW = events.length > 0 ? Math.max(ZZ_SLOT_W, (railWidth - ZZ_CARD_W - 4) / events.length) : ZZ_SLOT_W;

  // Horizontal timeline format (persisted with the HUD style).
  const format = hud.style.storyFormat;
  const updateHudStyle = useNarrativeStore((s) => s.updateHudStyle);
  const [formatMenuAnchor, setFormatMenuAnchor] = useState<HTMLElement | null>(null);
  const mapLabels = useNarrativeStore((s) => s.storyMapLabels);
  const setMapLabels = useNarrativeStore((s) => s.setStoryMapLabels);
  // Dwell vs travel colours - shared with the map badges and time-share bar.
  const { secondary } = hud;
  const kindColor = (e: StoryEvent) => (e.type === 'dwell' ? accent : e.type === 'camera' ? cameraColor(e) : secondary);

  const { tokens, accent, fs, inset } = hud;
  const getDoc = () => buildStoryDoc(events, entities, aiSummary, aiFocus);

  const activeIndex = useMemo(() => {
    if (!currentTime || events.length === 0) return -1;
    if (isFullExtent) return 0;
    const t = currentTime.getTime();
    return events.findIndex((e) => t >= e.startTime.getTime() && t <= e.endTime.getTime());
  }, [events, currentTime, isFullExtent]);

  useEffect(() => {
    if (minimized) return;
    activeRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
  }, [activeIndex, minimized, docked]);

  const stats = useMemo(() => {
    const travel = events.filter((e) => e.type === 'travel');
    const dwell = events.filter((e) => e.type === 'dwell');
    const secs = (e: StoryEvent) => (e.endTime.getTime() - e.startTime.getTime()) / 1000;
    return {
      travel: travel.length,
      dwell: dwell.length,
      cameras: events.filter((e) => e.type === 'camera').length,
      distanceKm: travel.reduce((s, e) => s + (e.distance || 0), 0),
      dwellSecs: dwell.reduce((s, e) => s + secs(e), 0),
      span:
        events.length > 0
          ? `${fmtDay(events[0].startTime)} – ${fmtDay(events[events.length - 1].endTime)}`
          : '',
    };
  }, [events]);

  if (minimized) {
    const label =
      events.length > 0 ? `${events.length}${croppedOut ? `/${allEvents.length}` : ''} events${activeIndex >= 0 ? ` · #${activeIndex + 1}` : ''}` : 'Story';
    return (
      <HudChip
        id="story"
        icon={<StoryIcon sx={{ fontSize: 16 }} />}
        label={label}
        tooltip="Show Story panel"
        defaultSx={STORY_DEFAULT}
        zIndex={Z_STORY}
        onExpand={() => setMinimized('story', false)}
      />
    );
  }

  const subtitle =
    entities.length > 0
      ? `${entities.join(', ')}${croppedOut ? ` · ${events.length} of ${allEvents.length} in view` : ''}${stats.span ? ` · ${stats.span}` : ''}${
          stats.dwellSecs > 0 ? ` · ${formatDuration(stats.dwellSecs)} dwelling` : ''
        }`
      : undefined;

  // Docked indicators: equal-width pills, centred in the header row.
  const indicatorPills =
    docked && hud.style.showStats && events.length > 0 ? (
      <Box sx={{ display: 'inline-grid', gridAutoFlow: 'column', gridAutoColumns: '1fr', gap: 0.75 }}>
        {[
          { k: 'Events', v: String(events.length) },
          { k: 'Dwells', v: String(stats.dwell) },
          { k: 'Trips', v: String(stats.travel) },
          { k: 'Distance', v: formatMiles(stats.distanceKm) },
          { k: 'Dwelling', v: formatDuration(stats.dwellSecs) },
          ...(stats.cameras ? [{ k: 'Cameras', v: String(stats.cameras) }] : []),
          ...(stats.span ? [{ k: 'Span', v: stats.span }] : []),
        ].map((s) => (
          <Box
            key={s.k}
            sx={{
              display: 'flex',
              alignItems: 'baseline',
              justifyContent: 'center',
              gap: 0.6,
              px: 1.25,
              height: 22,
              borderRadius: 999,
              bgcolor: inset,
              border: `1px solid ${tokens.border}`,
              whiteSpace: 'nowrap',
            }}
          >
            <Typography sx={{ fontSize: fs(0.56), color: tokens.muted, letterSpacing: 0.6, textTransform: 'uppercase', fontFamily: 'inherit', lineHeight: '22px' }}>
              {s.k}
            </Typography>
            <Typography sx={{ fontSize: fs(0.74), fontWeight: 700, color: accent, fontFamily: 'inherit', lineHeight: '22px' }}>
              {s.v}
            </Typography>
          </Box>
        ))}
      </Box>
    ) : null;

  // Event cards are shared between the vertical rail (float) and the
  // horizontal strip (dock).
  const renderEvent = (event: StoryEvent, idx: number) => {
    const isActive = idx === activeIndex;
    const isSelected = event.id === selectedId;
    const secs = (event.endTime.getTime() - event.startTime.getTime()) / 1000;
    const isDwell = event.type === 'dwell';
    const isCamera = event.type === 'camera';
    const future = currentTime && !isFullExtent && currentTime < event.startTime;
    const Icon = isDwell ? DwellIcon : isCamera ? CameraIcon : TravelIcon;
    const headline = isDwell
      ? `Dwelling ${formatDuration(event.duration ?? secs)}`
      : isCamera
        ? `Camera ${event.address || event.cameraId || ''}`
        : `Traveling${event.distance !== undefined ? ` ${formatMiles(event.distance)}` : ''}`;
    const detail = isDwell
      ? event.address
      : isCamera
        ? [event.category, event.distanceM !== undefined ? `${fmtMeters(event.distanceM)} from track` : ''].filter(Boolean).join(' · ')
        : `${formatDuration(secs)}${event.pointCount ? ` · ${event.pointCount} pts` : ' · inferred'}`;
    const badge =
      event.visitCount && event.visitCount > 1
        ? `${event.visitCount} visits`
        : event.tripCount && event.tripCount > 1
          ? `${event.tripCount} trips`
          : isCamera && event.hits && event.hits > 1
            ? `${event.hits} hits`
            : null;
    const kc = kindColor(event);
    const marker = (
      <Box
        sx={{
          width: 22,
          height: 22,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: isActive ? kc : hud.bg,
          color: isActive ? (isDwell ? tokens.onAccent : isCamera ? '#fff' : '#111') : kc,
          border: `1.5px solid ${isActive ? kc : tokens.border}`,
          boxShadow: isActive ? `0 0 0 4px ${kc}33, 0 0 14px ${kc}88` : 'none',
          transition: 'all 0.2s',
          zIndex: 1,
          flexShrink: 0,
        }}
      >
        <Icon sx={{ fontSize: 13 }} />
      </Box>
    );
    const body = (
      <Box
        className="pf-nar-card"
        sx={{
          px: 1,
          py: 0.75,
          borderRadius: 1.25,
          border: `1px solid ${isActive || isSelected ? kc : tokens.border}`,
          borderLeft: `3px solid ${kc}`,
          // Docked cards overlap the rail, so they need an opaque-ish surface.
          bgcolor: isActive ? `${kc}1f` : docked ? hud.bg : inset,
          transition: 'all 0.15s',
          minWidth: 0,
          ...(docked ? { width: ZZ_CARD_W } : { mb: 0.75 }),
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
          <Typography sx={{ fontSize: fs(0.62), color: tokens.muted, fontFamily: 'inherit', flexShrink: 0 }}>
            #{eventNo(event, idx)}
          </Typography>
          <Typography noWrap sx={{ fontSize: fs(0.82), fontWeight: 700, fontFamily: 'inherit', flex: 1, minWidth: 0 }}>
            {headline}
          </Typography>
          {badge && (
            <Typography
              sx={{
                fontSize: fs(0.6),
                fontWeight: 700,
                color: accent,
                border: `1px solid ${accent}`,
                borderRadius: 999,
                px: 0.6,
                lineHeight: 1.5,
                fontFamily: 'inherit',
                flexShrink: 0,
              }}
            >
              {badge}
            </Typography>
          )}
        </Box>
        <Typography sx={{ fontSize: fs(0.7), color: tokens.muted, fontFamily: 'inherit' }}>
          {fmtDay(event.startTime)} {fmtTime(event.startTime)}{isCamera ? '' : ` → ${fmtTime(event.endTime)}`}
        </Typography>
        {detail && (
          <Typography noWrap sx={{ fontSize: fs(0.7), color: tokens.muted, fontFamily: 'inherit' }}>
            {detail}
          </Typography>
        )}
      </Box>
    );
    const onClick = () => {
      setSelectedId(isSelected ? null : event.id);
      onEventSelect?.(event);
    };

    if (docked) {
      // Zig-zag: markers sit on a centre rail; cards alternate above/below,
      // joined by a 45° connector, so neighbours can overlap horizontally.
      const above = idx % 2 === 0;
      const x = idx * slotW + slotW / 2;
      const lineColor = isActive || isSelected ? accent : tokens.border;
      return (
        <Box
          key={event.id}
          ref={isActive ? activeRef : undefined}
          onClick={onClick}
          sx={{
            position: 'absolute',
            left: x,
            top: 0,
            bottom: 0,
            width: 0,
            cursor: onEventSelect ? 'pointer' : 'default',
            opacity: future ? 0.45 : 1,
            zIndex: isActive || isSelected ? 2 : 1,
            '&:hover .pf-nar-card': { borderColor: accent },
            '&:hover': { zIndex: 3 },
          }}
        >
          <Box sx={{ position: 'absolute', left: -11, top: '50%', transform: 'translateY(-50%)' }}>{marker}</Box>
          <Box
            sx={{
              position: 'absolute',
              left: 0,
              top: '50%',
              width: ZZ_DIAG * Math.SQRT2,
              height: 2,
              bgcolor: lineColor,
              transformOrigin: 'left center',
              transform: `translateY(-1px) rotate(${above ? -45 : 45}deg)`,
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              left: ZZ_DIAG,
              width: ZZ_CARD_W,
              ...(above ? { bottom: `calc(50% + ${ZZ_DIAG}px)` } : { top: `calc(50% + ${ZZ_DIAG}px)` }),
            }}
          >
            {body}
          </Box>
        </Box>
      );
    }

    return (
      <Box
        key={event.id}
        ref={isActive ? activeRef : undefined}
        onClick={onClick}
        sx={{
          display: 'grid',
          gridTemplateColumns: '22px 1fr',
          columnGap: 1,
          position: 'relative',
          cursor: onEventSelect ? 'pointer' : 'default',
          opacity: future ? 0.45 : 1,
          '&:hover .pf-nar-card': { borderColor: accent },
        }}
      >
        <Box sx={{ position: 'relative', display: 'flex', justifyContent: 'center', pt: 0.75 }}>
          {idx < events.length - 1 && (
            <Box
              sx={{
                position: 'absolute',
                top: 22,
                bottom: -6,
                width: 2,
                bgcolor: isActive ? accent : tokens.border,
                opacity: isActive ? 0.7 : 1,
              }}
            />
          )}
          {marker}
        </Box>
        {body}
      </Box>
    );
  };

  /* ---- Docked formats ------------------------------------------------- */

  const scrollerSx = {
    position: 'relative' as const,
    overflowX: 'auto' as const,
    overflowY: 'hidden' as const,
    flex: 1,
    minHeight: 0,
    minWidth: 0,
    userSelect: 'none' as const,
    '&::-webkit-scrollbar': { height: 6 },
    '&::-webkit-scrollbar-thumb': { bgcolor: tokens.border, borderRadius: 3 },
  };
  const eventState = (event: StoryEvent, idx: number) => ({
    isActive: idx === activeIndex,
    isSelected: event.id === selectedId,
    future: Boolean(currentTime && !isFullExtent && currentTime < event.startTime),
  });
  const selectEvent = (event: StoryEvent) => {
    setSelectedId((cur) => (cur === event.id ? null : event.id));
    onEventSelect?.(event);
  };
  const typeLabel = (event: StoryEvent, idx: number) => {
    const Icon = event.type === 'dwell' ? DwellIcon : event.type === 'camera' ? CameraIcon : TravelIcon;
    const badge =
      event.visitCount && event.visitCount > 1
        ? `${event.visitCount} visits`
        : event.tripCount && event.tripCount > 1
          ? `${event.tripCount} trips`
          : event.type === 'camera' && event.hits && event.hits > 1
            ? `${event.hits} hits`
            : null;
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
        <Typography sx={{ fontSize: fs(0.6), color: tokens.muted, fontFamily: 'inherit' }}>#{eventNo(event, idx)}</Typography>
        <Icon sx={{ fontSize: 13, color: kindColor(event) }} />
        <Typography noWrap sx={{ fontSize: fs(0.74), fontWeight: 700, fontFamily: 'inherit' }}>
          {kindName(event)}
        </Typography>
        {badge && (
          <Typography
            sx={{ ml: 'auto', fontSize: fs(0.56), fontWeight: 700, color: accent, border: `1px solid ${accent}`, borderRadius: 999, px: 0.6, lineHeight: 1.5, fontFamily: 'inherit', flexShrink: 0 }}
          >
            {badge}
          </Typography>
        )}
      </Box>
    );
  };

  /** Ledger: one column per event, labelled rows. */
  const renderLedger = () => {
    const maxSecs = Math.max(1, ...events.map(eventSecs));
    // Spreadsheet-style banding on alternate rows.
    const stripe = hud.style.theme === 'paper' ? 'rgba(0, 0, 0, 0.045)' : 'rgba(255, 255, 255, 0.045)';
    const colW = Math.max(LEDGER_COL_MIN, (railWidth - LEDGER_LABEL_W - 4) / Math.max(1, events.length));
    const rows: Array<{ label: string; cell: (e: StoryEvent, i: number) => React.ReactNode }> = [
      { label: 'Segment', cell: (e, i) => typeLabel(e, i) },
      {
        label: 'Duration',
        cell: (e) => {
          const s = eventSecs(e);
          if (e.type === 'camera') {
            return (
              <Typography noWrap sx={{ fontSize: fs(0.72), fontFamily: 'inherit', color: tokens.muted }}>
                pass{e.hits && e.hits > 1 ? ` · ${e.hits} hits` : ''}
              </Typography>
            );
          }
          return (
            <Box>
              <Typography sx={{ fontSize: fs(0.8), fontWeight: 700, fontFamily: 'inherit', lineHeight: 1.2 }}>
                {formatDuration(e.type === 'dwell' ? (e.duration ?? s) : s)}
              </Typography>
              <Box sx={{ mt: 0.4, height: 3, borderRadius: 2, bgcolor: inset, overflow: 'hidden' }}>
                <Box sx={{ width: `${Math.max(3, (s / maxSecs) * 100)}%`, height: '100%', bgcolor: kindColor(e) }} />
              </Box>
            </Box>
          );
        },
      },
      {
        label: 'Window',
        cell: (e) => (
          <Typography noWrap sx={{ fontSize: fs(0.68), fontFamily: 'inherit' }}>
            {e.type === 'camera' ? fmtTime(e.startTime) : `${fmtTime(e.startTime)} → ${fmtTime(e.endTime)}`}
          </Typography>
        ),
      },
      {
        label: 'Where',
        cell: (e) => (
          <Typography noWrap sx={{ fontSize: fs(0.68), fontFamily: 'inherit', fontWeight: e.type === 'travel' ? 700 : 400 }}>
            {e.type === 'camera' ? e.address || e.cameraId || fmtCoord(e.location) || '—' : e.type === 'dwell' ? (e.address || fmtCoord(e.location) || '—') : e.distance !== undefined ? formatMiles(e.distance) : 'inferred'}
          </Typography>
        ),
      },
      {
        label: 'Track',
        cell: (e) => {
          if (e.type === 'camera') {
            return (
              <Typography noWrap sx={{ fontSize: fs(0.68), color: tokens.muted, fontFamily: 'inherit' }}>
                {[e.category, e.distanceM !== undefined ? `${fmtMeters(e.distanceM)} off track` : ''].filter(Boolean).join(' · ') || '—'}
              </Typography>
            );
          }
          const mph = avgMph(e);
          return (
            <Typography noWrap sx={{ fontSize: fs(0.68), color: tokens.muted, fontFamily: 'inherit' }}>
              {e.pointCount ? `${e.pointCount} pts${mph ? ` · ${Math.round(mph)} mph avg` : ''}` : '—'}
            </Typography>
          );
        },
      },
    ];
    return (
      <Box ref={railRef} sx={scrollerSx}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: `${LEDGER_LABEL_W}px repeat(${events.length}, ${colW}px)`,
            gridAutoRows: 'minmax(0, 1fr)',
            height: '100%',
            minWidth: '100%',
            width: 'max-content',
          }}
        >
          {rows.map((row, r) => (
            <React.Fragment key={row.label}>
              <Box
                sx={{
                  position: 'sticky',
                  left: 0,
                  zIndex: 2,
                  bgcolor: hud.bg,
                  boxShadow: r % 2 === 1 ? `inset 0 0 0 999px ${stripe}` : 'none',
                  display: 'flex',
                  alignItems: 'center',
                  pr: 1,
                  borderBottom: r < rows.length - 1 ? `1px solid ${tokens.border}` : 'none',
                }}
              >
                <Typography sx={{ fontSize: fs(0.54), color: tokens.muted, letterSpacing: 0.8, textTransform: 'uppercase', fontFamily: 'inherit' }}>
                  {row.label}
                </Typography>
              </Box>
              {events.map((e, i) => {
                const { isActive, isSelected, future } = eventState(e, i);
                const hi = isActive || isSelected;
                return (
                  <Box
                    key={`${row.label}-${e.id}`}
                    ref={r === 0 && isActive ? activeRef : undefined}
                    onClick={() => selectEvent(e)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      px: 1,
                      minWidth: 0,
                      cursor: onEventSelect ? 'pointer' : 'default',
                      opacity: future ? 0.45 : 1,
                      bgcolor: hi ? `${kindColor(e)}1a` : r % 2 === 1 ? stripe : 'transparent',
                      borderLeft: `1px solid ${hi ? kindColor(e) : tokens.border}`,
                      borderRight: hi ? `1px solid ${kindColor(e)}` : 'none',
                      borderTop: r === 0 ? `2px solid ${hi ? kindColor(e) : `${kindColor(e)}66`}` : 'none',
                      borderBottom: r < rows.length - 1 ? `1px solid ${tokens.border}` : hi ? `2px solid ${kindColor(e)}` : 'none',
                      '&:hover': { bgcolor: `${kindColor(e)}12` },
                    }}
                  >
                    <Box sx={{ minWidth: 0, flex: 1 }}>{row.cell(e, i)}</Box>
                  </Box>
                );
              })}
            </React.Fragment>
          ))}
        </Box>
      </Box>
    );
  };

  /** Stops & legs: tall dwell tiles joined by compact travel legs + time-share bar. */
  const renderStops = () => {
    const t0 = events.length ? events[0].startTime.getTime() : 0;
    const t1 = events.length ? events[events.length - 1].endTime.getTime() : 1;
    const span = Math.max(1, t1 - t0);
    const nowPct = currentTime && !isFullExtent ? Math.min(100, Math.max(0, ((currentTime.getTime() - t0) / span) * 100)) : null;
    type StopChunk =
      | { kind: 'one'; event: StoryEvent; idx: number }
      | { kind: 'cameras'; items: Array<{ event: StoryEvent; idx: number }> };
    const chunks: StopChunk[] = [];
    for (let i = 0; i < events.length; i++) {
      if (events[i].type === 'camera') {
        const items = [{ event: events[i], idx: i }];
        while (i + 1 < events.length && events[i + 1].type === 'camera') {
          i += 1;
          items.push({ event: events[i], idx: i });
        }
        chunks.push({ kind: 'cameras', items });
      } else {
        chunks.push({ kind: 'one', event: events[i], idx: i });
      }
    }
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, minWidth: 0 }}>
        <Box ref={railRef} sx={scrollerSx}>
          <Box sx={{ display: 'flex', alignItems: 'stretch', height: '100%', minWidth: '100%', width: 'max-content', gap: 0.75, pb: 0.5 }}>
            {chunks.map((chunk) => {
              if (chunk.kind === 'cameras') {
                const anyHi = chunk.items.some(({ event, idx }) => {
                  const s = eventState(event, idx);
                  return s.isActive || s.isSelected;
                });
                const width = Math.max(CAM_TILE_W, chunk.items.length * CAM_DOT_W);
                return (
                  <Box
                    key={chunk.items.map(({ event }) => event.id).join('|')}
                    sx={{
                      flex: `0 0 ${width}px`,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      position: 'relative',
                      px: 0.5,
                    }}
                  >
                    <Box sx={{ position: 'absolute', left: -6, right: -6, top: '42%', height: 2, bgcolor: secondary, opacity: anyHi ? 1 : 0.55 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-evenly', alignItems: 'flex-start', position: 'relative', zIndex: 1, pt: 0.25 }}>
                      {chunk.items.map(({ event, idx }) => {
                        const { isActive, isSelected, future } = eventState(event, idx);
                        const hi = isActive || isSelected;
                        const cc = cameraColor(event);
                        const n = eventNo(event, idx);
                        return (
                          <Tooltip
                            key={event.id}
                            title={`#${n} Camera ${event.address || event.cameraId || ''}${event.category ? ` · ${event.category}` : ''}`}
                            enterDelay={300}
                          >
                            <Box
                              ref={isActive ? activeRef : undefined}
                              onClick={() => selectEvent(event)}
                              sx={{
                                width: CAM_DOT_W - 4,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                cursor: onEventSelect ? 'pointer' : 'default',
                                opacity: future ? 0.45 : 1,
                              }}
                            >
                              <Box
                                sx={{
                                  minWidth: 22,
                                  height: 22,
                                  px: 0.5,
                                  borderRadius: 999,
                                  bgcolor: hi ? cc : hud.bg,
                                  border: `2px solid ${cc}`,
                                  color: hi ? '#fff' : cc,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: fs(0.62),
                                  fontWeight: 800,
                                  fontFamily: 'inherit',
                                  boxShadow: isActive ? `0 0 0 3px ${cc}33` : 'none',
                                }}
                              >
                                {n}
                              </Box>
                              <Typography sx={{ fontSize: fs(0.58), fontWeight: 700, fontFamily: 'inherit', mt: 0.35, lineHeight: 1.1 }}>
                                {fmtTime(event.startTime)}
                              </Typography>
                            </Box>
                          </Tooltip>
                        );
                      })}
                    </Box>
                    <Typography noWrap sx={{ fontSize: fs(0.52), color: tokens.muted, fontFamily: 'inherit', textAlign: 'center', mt: 0.35, position: 'relative', zIndex: 1 }}>
                      {chunk.items.length === 1
                        ? (chunk.items[0].event.address || chunk.items[0].event.cameraId || 'Camera')
                        : `${chunk.items.length} cameras`}
                    </Typography>
                  </Box>
                );
              }
              const e = chunk.event;
              const i = chunk.idx;
              const { isActive, isSelected, future } = eventState(e, i);
              const hi = isActive || isSelected;
              const s = eventSecs(e);
              if (e.type === 'dwell') {
                return (
                  <Box
                    key={e.id}
                    ref={isActive ? activeRef : undefined}
                    onClick={() => selectEvent(e)}
                    sx={{
                      flex: `1 1 ${STOP_TILE_W}px`,
                      minWidth: STOP_TILE_W,
                      maxWidth: STOP_TILE_W * 1.6,
                      display: 'flex',
                      flexDirection: 'column',
                      px: 1.25,
                      py: 0.75,
                      borderRadius: 1.25,
                      border: `1px solid ${hi ? accent : `${accent}55`}`,
                      borderTop: `3px solid ${accent}`,
                      bgcolor: hi ? `${accent}1f` : inset,
                      boxShadow: isActive ? `0 0 0 3px ${accent}26` : 'none',
                      cursor: onEventSelect ? 'pointer' : 'default',
                      opacity: future ? 0.45 : 1,
                      '&:hover': { borderColor: accent },
                    }}
                  >
                    {typeLabel(e, i)}
                    <Typography sx={{ fontSize: fs(1.25), fontWeight: 800, fontFamily: 'inherit', lineHeight: 1.1, mt: 0.25 }}>
                      {formatDuration(e.duration ?? s)}
                    </Typography>
                    <Typography sx={{ fontSize: fs(0.78), fontWeight: 700, fontFamily: 'inherit', color: tokens.text, mt: 0.15 }}>
                      {fmtTime(e.startTime)}
                    </Typography>
                    {dwellPlace(e) && (
                      <Typography
                        sx={{
                          fontSize: fs(0.68),
                          color: tokens.muted,
                          fontFamily: 'inherit',
                          mt: 0.35,
                          lineHeight: 1.25,
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}
                      >
                        {dwellPlace(e)}
                      </Typography>
                    )}
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'end', columnGap: 0.75, mt: 'auto' }}>
                      <Box>
                        <Typography sx={{ fontSize: fs(0.52), color: tokens.muted, letterSpacing: 0.8, textTransform: 'uppercase', fontFamily: 'inherit' }}>Arrive</Typography>
                        <Typography sx={{ fontSize: fs(0.72), fontWeight: 700, fontFamily: 'inherit' }}>{fmtTime(e.startTime)}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, pb: 0.5 }}>
                        <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: accent }} />
                        <Box sx={{ width: 18, height: 1, bgcolor: tokens.border }} />
                        <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: accent }} />
                      </Box>
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography sx={{ fontSize: fs(0.52), color: tokens.muted, letterSpacing: 0.8, textTransform: 'uppercase', fontFamily: 'inherit' }}>Depart</Typography>
                        <Typography sx={{ fontSize: fs(0.72), fontWeight: 700, fontFamily: 'inherit' }}>{fmtTime(e.endTime)}</Typography>
                      </Box>
                    </Box>
                  </Box>
                );
              }
              const mph = avgMph(e);
              return (
                <Box
                  key={e.id}
                  ref={isActive ? activeRef : undefined}
                  onClick={() => selectEvent(e)}
                  sx={{
                    flex: `0 0 ${LEG_W}px`,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    px: 0.5,
                    cursor: onEventSelect ? 'pointer' : 'default',
                    opacity: future ? 0.45 : 1,
                    position: 'relative',
                    '&:hover .pf-leg': { borderColor: secondary },
                  }}
                >
                  <Box
                    className="pf-leg"
                    sx={{
                      width: '100%',
                      borderRadius: 1,
                      border: `1px solid ${hi ? secondary : `${secondary}66`}`,
                      borderTop: `3px solid ${secondary}`,
                      bgcolor: hi ? `${secondary}1f` : hud.bg,
                      px: 0.75,
                      py: 0.5,
                      textAlign: 'center',
                      position: 'relative',
                      zIndex: 1,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.4 }}>
                      <Typography sx={{ fontSize: fs(0.56), color: tokens.muted, fontFamily: 'inherit' }}>#{eventNo(e, i)}</Typography>
                      <TravelIcon sx={{ fontSize: 12, color: secondary }} />
                    </Box>
                    <Typography noWrap sx={{ fontSize: fs(0.76), fontWeight: 800, fontFamily: 'inherit' }}>
                      {e.distance !== undefined ? formatMiles(e.distance) : 'inferred'}
                    </Typography>
                    <Typography noWrap sx={{ fontSize: fs(0.62), color: tokens.muted, fontFamily: 'inherit' }}>
                      {formatDuration(s)}
                      {e.tripCount && e.tripCount > 1 ? ` · ${e.tripCount} trips` : ''}
                    </Typography>
                    <Typography noWrap sx={{ fontSize: fs(0.58), color: tokens.muted, fontFamily: 'inherit' }}>
                      {e.pointCount ? `${e.pointCount} pts${mph ? ` · ${Math.round(mph)} mph` : ''}` : '—'}
                    </Typography>
                  </Box>
                  {/* connector line behind the leg */}
                  <Box sx={{ position: 'absolute', left: -6, right: -6, top: '50%', height: 2, bgcolor: secondary, opacity: hi ? 1 : 0.6 }} />
                </Box>
              );
            })}
          </Box>
        </Box>

        {/* Time-share bar: dwell vs travel, proportional to duration. */}
        <Box sx={{ flexShrink: 0, pt: 0.5 }}>
          <Box sx={{ position: 'relative', height: 8, borderRadius: 4, overflow: 'hidden', display: 'flex', bgcolor: inset }}>
            {events.map((e, i) => {
              const { isActive, isSelected } = eventState(e, i);
              const w = e.type === 'camera' ? 0.004 : (eventSecs(e) * 1000) / span;
              return (
                <Tooltip key={e.id} title={`#${eventNo(e, i)} ${kindName(e)}${e.type === 'camera' ? '' : ` ${formatDuration(eventSecs(e))}`}`} enterDelay={300}>
                  <Box
                    onClick={() => selectEvent(e)}
                    sx={{
                      width: `${w * 100}%`,
                      height: '100%',
                      bgcolor: kindColor(e),
                      opacity: isActive || isSelected ? 1 : 0.75,
                      borderRight: `1px solid ${hud.bg}`,
                      cursor: 'pointer',
                      '&:hover': { opacity: 1 },
                    }}
                  />
                </Tooltip>
              );
            })}
            {nowPct !== null && (
              <Box sx={{ position: 'absolute', left: `${nowPct}%`, top: -1, bottom: -1, width: 2, bgcolor: tokens.text, boxShadow: `0 0 4px ${tokens.text}` }} />
            )}
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.25 }}>
            <Typography sx={{ fontSize: fs(0.56), color: tokens.muted, fontFamily: 'inherit' }}>
              {events.length ? `${fmtDay(events[0].startTime)} ${fmtTime(events[0].startTime)}` : ''}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Box sx={{ width: 8, height: 8, borderRadius: 1, bgcolor: accent }} />
              <Typography sx={{ fontSize: fs(0.56), color: tokens.muted, letterSpacing: 0.6, textTransform: 'uppercase', fontFamily: 'inherit' }}>
                dwell
              </Typography>
              <Box sx={{ width: 8, height: 8, borderRadius: 1, bgcolor: secondary, ml: 0.5 }} />
              <Typography sx={{ fontSize: fs(0.56), color: tokens.muted, letterSpacing: 0.6, textTransform: 'uppercase', fontFamily: 'inherit' }}>
                travel
              </Typography>
              {stats.cameras > 0 && (
                <>
                  <CameraIcon sx={{ fontSize: 10, color: tokens.muted, ml: 0.5 }} />
                  <Typography sx={{ fontSize: fs(0.56), color: tokens.muted, letterSpacing: 0.6, textTransform: 'uppercase', fontFamily: 'inherit' }}>
                    camera
                  </Typography>
                </>
              )}
            </Box>
            <Typography sx={{ fontSize: fs(0.56), color: tokens.muted, fontFamily: 'inherit' }}>
              {events.length ? `${fmtDay(events[events.length - 1].endTime)} ${fmtTime(events[events.length - 1].endTime)}` : ''}
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  };

  /** Zig-zag: centre rail with alternating cards (renderEvent handles each). */
  const renderZigzag = () => (
    <Box ref={railRef} sx={scrollerSx}>
      <Box
        sx={{
          position: 'relative',
          height: '100%',
          minWidth: '100%',
          width: events.length * slotW + ZZ_CARD_W,
        }}
      >
        <Box sx={{ position: 'absolute', left: 0, right: 0, top: '50%', height: 2, bgcolor: tokens.border, transform: 'translateY(-50%)' }} />
        {activeIndex >= 0 && (
          <Box
            sx={{
              position: 'absolute',
              left: 0,
              width: activeIndex * slotW + slotW / 2,
              top: '50%',
              height: 2,
              bgcolor: accent,
              opacity: 0.7,
              transform: 'translateY(-50%)',
              transition: 'width 0.3s',
            }}
          />
        )}
        {events.map(renderEvent)}
      </Box>
    </Box>
  );

  const renderDockedTimeline = () => (format === 'ledger' ? renderLedger() : format === 'stops' ? renderStops() : renderZigzag());

  return (
    <Box
      ref={cardRef}
      data-hud-card=""
      onClick={(e) => e.stopPropagation()}
      sx={cardFrameSx(hud, drag, dock, Z_STORY, floatHeight)}
    >
      {/* Height resize: drag the top edge when docked, the bottom edge when floating. */}
      <ResizeHandle
        edge={docked ? 'top' : 'bottom'}
        color={accent}
        min={docked ? STORY_DOCK_HEIGHT_MIN : STORY_FLOAT_HEIGHT_MIN}
        getStartHeight={() => cardRef.current?.offsetHeight ?? (docked ? hud.style.storyDockHeight : 0)}
        onResize={(h) => updateHudStyle(docked ? { storyDockHeight: h } : { storyFloatHeight: h })}
      />
      <AccentStrip hud={hud} />
      <CardHeader

        hud={hud}
        panelId="story"
        icon={<StoryIcon sx={{ fontSize: 18 }} />}
        title="Story"
        subtitle={subtitle}
        handleProps={drag.handleProps}
        docked={docked}
        getDoc={getDoc}
        cardRef={cardRef}
        exportDisabled={events.length === 0}
        center={indicatorPills}
        extra={
          allEvents.length > 0 ? (
            <Box sx={{ display: 'flex', alignItems: 'center', mr: 0.5 }}>
              <Tooltip title={cropToView ? 'Showing events in the map view - click to show all' : 'Show all events - click to crop to the map view'}>
                <IconButton
                  size="small"
                  onClick={() => setCropToView(!cropToView)}
                  sx={{
                    p: 0.5,
                    borderRadius: 1,
                    color: cropToView ? accent : tokens.muted,
                    bgcolor: cropToView ? `${accent}22` : 'transparent',
                    '&:hover': { color: accent },
                  }}
                >
                  <CropIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
              {cameraPassesAvailable && (
                <Tooltip title={showCameras ? 'Cameras passed are in the sequence - click to hide' : 'Add cameras passed to the sequence'}>
                  <IconButton
                    size="small"
                    onClick={() => setShowCameras(!showCameras)}
                    sx={{
                      p: 0.5,
                      borderRadius: 1,
                      color: showCameras ? accent : tokens.muted,
                      bgcolor: showCameras ? `${accent}22` : 'transparent',
                      '&:hover': { color: accent },
                    }}
                  >
                    <CameraIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title={mapLabels ? 'Hide event numbers on map' : 'Show event numbers on map'}>
                <IconButton
                  size="small"
                  onClick={() => setMapLabels(!mapLabels)}
                  sx={{
                    p: 0.5,
                    borderRadius: 1,
                    color: mapLabels ? accent : tokens.muted,
                    bgcolor: mapLabels ? `${accent}22` : 'transparent',
                    '&:hover': { color: accent },
                  }}
                >
                  <MapLabelsIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
              {docked && (
                <>
                  <Tooltip title={`Timeline format: ${STORY_TIMELINE_FORMATS.find((f) => f.id === format)?.label ?? ''}`}>
                    <IconButton
                      size="small"
                      onClick={(e) => setFormatMenuAnchor(e.currentTarget)}
                      sx={{ p: 0.5, color: tokens.muted, '&:hover': { color: accent } }}
                    >
                      <FormatIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                  <Menu
                    anchorEl={formatMenuAnchor}
                    open={Boolean(formatMenuAnchor)}
                    onClose={() => setFormatMenuAnchor(null)}
                    container={hudPortalContainer}
                    sx={{ zIndex: 4000 }}
                    PaperProps={{ sx: { bgcolor: 'rgba(24,26,32,0.97)', color: '#fff', minWidth: 240 } }}
                  >
                    {STORY_TIMELINE_FORMATS.map((f) => (
                      <MenuItem
                        key={f.id}
                        dense
                        selected={f.id === format}
                        onClick={() => {
                          updateHudStyle({ storyFormat: f.id });
                          setFormatMenuAnchor(null);
                        }}
                      >
                        <ListItemIcon sx={{ color: 'inherit', minWidth: 28 }}>
                          {f.id === format ? <CheckIcon fontSize="small" /> : <Box sx={{ width: 20 }} />}
                        </ListItemIcon>
                        <ListItemText primary={f.label} secondary={f.hint} secondaryTypographyProps={{ sx: { color: '#aaa' } }} />
                      </MenuItem>
                    ))}
                  </Menu>
                </>
              )}
              <Tooltip title={aiPanelOpen ? 'AI summary panel is open' : 'Open AI summary panel'}>
                <IconButton
                  size="small"
                  onClick={onOpenAi}
                  sx={{
                    p: 0.5,
                    borderRadius: 1,
                    color: aiPanelOpen ? accent : tokens.muted,
                    bgcolor: aiPanelOpen ? `${accent}22` : 'transparent',
                    '&:hover': { color: accent },
                  }}
                >
                  <AiIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
              {activeIndex >= 0 && (
                <Tooltip title="Jump to current event">
                  <IconButton
                    size="small"
                    sx={{ color: tokens.muted, p: 0.5, '&:hover': { color: accent } }}
                    onClick={() => {
                      activeRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
                    }}
                  >
                    <EndIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          ) : null
        }
        onMinimize={() => setMinimized('story', true)}
        onClose={() => setPanelOpen('story', false)}
      />

      {allEvents.length > 0 && events.length === 0 ? (
        <Box sx={{ p: 2.5, textAlign: 'center' }}>
          <CropIcon sx={{ fontSize: 32, color: accent, opacity: 0.7, mb: 1 }} />
          <Typography sx={{ fontSize: fs(0.85), fontWeight: 600, fontFamily: 'inherit' }}>
            No story events in the current map view
          </Typography>
          <Typography sx={{ fontSize: fs(0.75), color: tokens.muted, mt: 0.5, fontFamily: 'inherit' }}>
            Pan or zoom out to bring events on screen, or show all {allEvents.length} events.
          </Typography>
          <Button
            size="small"
            variant="outlined"
            onClick={() => setCropToView(false)}
            sx={{ mt: 1.5, color: accent, borderColor: accent, textTransform: 'none', fontFamily: 'inherit' }}
          >
            Show all events
          </Button>
        </Box>
      ) : events.length === 0 ? (
        <Box sx={{ p: 2.5, textAlign: 'center' }}>
          <StoryIcon sx={{ fontSize: 36, color: accent, opacity: 0.7, mb: 1 }} />
          <Typography sx={{ fontSize: fs(0.85), fontWeight: 600, fontFamily: 'inherit' }}>
            No story timeline yet
          </Typography>
          <Typography sx={{ fontSize: fs(0.75), color: tokens.muted, mt: 0.5, fontFamily: 'inherit' }}>
            Select one entity and open Story Mode once to build the timeline. It stays here afterwards.
          </Typography>
          <Button
            size="small"
            variant="outlined"
            onClick={() => setActiveToolId('story-mode')}
            sx={{ mt: 1.5, color: accent, borderColor: accent, textTransform: 'none', fontFamily: 'inherit' }}
          >
            Open Story Mode
          </Button>
        </Box>
      ) : (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0,
            px: docked ? 1.5 : 0,
            pb: docked ? 0.75 : 0,
          }}
        >
          {/* Floating: stat tiles + text tabs. (Docked indicators live in the header.) */}
          {!docked && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, flexShrink: 0, px: 1.5, pb: 1 }}>
              {hud.style.showStats && (
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 0.5 }}>
                  {[
                    { k: 'Events', v: String(events.length) },
                    { k: 'Dwells', v: String(stats.dwell) },
                    { k: 'Trips', v: String(stats.travel) },
                    { k: 'Distance', v: formatMiles(stats.distanceKm) },
                  ].map((s) => (
                    <Box
                      key={s.k}
                      sx={{ borderRadius: 1, px: 0.75, py: 0.5, bgcolor: inset, border: `1px solid ${tokens.border}`, minWidth: 0 }}
                    >
                      <Typography sx={{ fontSize: fs(0.6), color: tokens.muted, letterSpacing: 0.6, textTransform: 'uppercase', fontFamily: 'inherit' }}>
                        {s.k}
                      </Typography>
                      <Typography noWrap sx={{ fontSize: fs(0.85), fontWeight: 700, color: accent, fontFamily: 'inherit' }}>
                        {s.v}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}

            </Box>
          )}

          {docked ? (
            renderDockedTimeline()
          ) : (
            <Box sx={{ overflowY: 'auto', px: 1.5, pb: 1.5, flex: 1, minHeight: 0 }}>{events.map(renderEvent)}</Box>
          )}        </Box>
      )}
    </Box>
  );
};

/* ------------------------------------------------------------------------ */

/* ------------------------------------------------------------------------ */
/* AI summary card (its own pane: drag / dock / resize independently)        */
/* ------------------------------------------------------------------------ */

const AiCard: React.FC<{
  onOpenPrompt: () => void;
  onRegenerate: () => void;
}> = ({ onOpenPrompt, onRegenerate }) => {
  const hud = useHudStyle();
  const drag = useHudDrag('ai', AI_DEFAULT);
  const minimized = useNarrativeStore((s) => s.panelMinimized.ai);
  const setMinimized = useNarrativeStore((s) => s.setPanelMinimized);
  const setPanelOpen = useNarrativeStore((s) => s.setPanelOpen);
  const layout = useNarrativeStore((s) => s.panelLayout.ai);
  const updateHudStyle = useNarrativeStore((s) => s.updateHudStyle);
  const entities = useNarrativeStore((s) => s.storyEntities);
  const aiSummary = useNarrativeStore((s) => s.storyAiSummary);
  const aiFocus = useNarrativeStore((s) => s.storyAiFocus);
  const aiLoading = useNarrativeStore((s) => s.storyAiLoading);
  const aiScope = useNarrativeStore((s) => s.storyAiScope);
  // Share the bottom strip with a docked Story card; otherwise span the whole width.
  const storyDocked = useNarrativeStore((s) => s.panelsOpen.story && !s.panelMinimized.story && s.panelLayout.story === 'dock');
  const cardRef = useRef<HTMLDivElement | null>(null);
  const { tokens, accent, fs } = hud;
  const docked = layout === 'dock';
  const dock: DockGeometry | null = docked
    ? storyDocked
      ? { bottom: 12, height: hud.style.aiDockHeight, left: 'auto', right: AI_DOCK_GAP, width: AI_DOCK_WIDTH }
      : { bottom: 12, height: hud.style.aiDockHeight }
    : null;
  const floatHeight = hud.style.aiFloatHeight;
  const getDoc = () => buildStoryAiDoc(entities, aiSummary, aiFocus);
  const detailLabel = hud.style.storyAiDetail === 'short' ? 'Short' : 'Detailed';

  if (minimized) {
    return (
      <HudChip
        id="ai"
        icon={<AiIcon sx={{ fontSize: 16 }} />}
        label="AI summary"
        tooltip="Show AI summary"
        defaultSx={AI_DEFAULT}
        zIndex={Z_AI}
        onExpand={() => setMinimized('ai', false)}
      />
    );
  }

  const headerButton = (label: string, icon: React.ReactNode, onClick: () => void, primary = false) => (
    <Tooltip title={label}>
      <span>
        <IconButton
          size="small"
          onClick={onClick}
          disabled={aiLoading}
          sx={{ p: 0.5, borderRadius: 1, color: primary ? accent : tokens.muted, '&:hover': { color: accent } }}
        >
          {icon}
        </IconButton>
      </span>
    </Tooltip>
  );

  return (
    <Box ref={cardRef} data-hud-card="" onClick={(e) => e.stopPropagation()} sx={cardFrameSx(hud, drag, dock, Z_AI, floatHeight)}>
      <ResizeHandle
        edge={docked ? 'top' : 'bottom'}
        color={accent}
        min={docked ? STORY_DOCK_HEIGHT_MIN : STORY_FLOAT_HEIGHT_MIN}
        getStartHeight={() => cardRef.current?.offsetHeight ?? (docked ? hud.style.aiDockHeight : 0)}
        onResize={(h) => updateHudStyle(docked ? { aiDockHeight: h } : { aiFloatHeight: h })}
      />
      <AccentStrip hud={hud} />
      <CardHeader
        hud={hud}
        panelId="ai"
        icon={<AiIcon sx={{ fontSize: 18 }} />}
        title="AI summary"
        subtitle={
          aiSummary || aiLoading
            ? `${detailLabel}${aiScope ? ` · ${aiScope}` : ''}${aiFocus ? ` · ${aiFocus}` : ''}`
            : entities.join(', ') || undefined
        }
        handleProps={drag.handleProps}
        docked={docked}
        getDoc={getDoc}
        cardRef={cardRef}
        exportDisabled={!aiSummary}
        extra={
          <Box sx={{ display: 'flex', alignItems: 'center', mr: 0.5 }}>
            {headerButton('New summary…', <AiIcon sx={{ fontSize: 16 }} />, onOpenPrompt, true)}
            {aiSummary ? headerButton('Regenerate', <RefreshIcon sx={{ fontSize: 16 }} />, onRegenerate) : null}
          </Box>
        }
        onMinimize={() => setMinimized('ai', true)}
        onClose={() => setPanelOpen('ai', false)}
      />
      <Box sx={{ overflowY: 'auto', flex: 1, minHeight: 0, minWidth: 0, px: 1.5, pb: 1.5, pt: 0.5 }}>
        {aiLoading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.75, py: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={18} sx={{ color: accent }} />
              <Typography sx={{ fontSize: fs(0.78), color: tokens.muted, fontFamily: 'inherit' }}>
                Analyzing loaded datasets…
              </Typography>
            </Box>
            {aiFocus && (
              <Typography sx={{ fontSize: fs(0.68), color: tokens.muted, fontStyle: 'italic', fontFamily: 'inherit', textAlign: 'center' }}>
                Focus: {aiFocus}
              </Typography>
            )}
          </Box>
        ) : aiSummary ? (
          <AiSummaryBody text={aiSummary} hud={hud} />
        ) : (
          <Box
            sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', minHeight: 96, px: 2, gap: 1 }}
          >
            <Typography sx={{ fontSize: fs(0.74), color: tokens.muted, fontFamily: 'inherit', textAlign: 'center' }}>
              No summary yet. Summarize the whole timeline, or tell the AI what to focus on.
            </Typography>
            <Button
              size="small"
              startIcon={<AiIcon />}
              onClick={onOpenPrompt}
              sx={{
                color: accent,
                border: `1px solid ${accent}66`,
                bgcolor: `${accent}1f`,
                textTransform: 'none',
                fontFamily: 'inherit',
                '&:hover': { bgcolor: `${accent}33` },
              }}
            >
              Generate summary…
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
};

/* ------------------------------------------------------------------------ */

const PathfinderMapNarrativeOverlay: React.FC<PathfinderMapNarrativeOverlayProps> = ({
  currentTime,
  isFullExtent = true,
  onEventSelect,
  dataPoints,
  routes,
  dwellLocations,
  actualDevices,
  itinerarySubmissions,
  coTravelerResults,
  signalData,
}) => {
  const hud = useHudStyle();
  const storyOpen = useNarrativeStore((s) => s.panelsOpen.story);
  const aiOpen = useNarrativeStore((s) => s.panelsOpen.ai);
  const setPanelOpen = useNarrativeStore((s) => s.setPanelOpen);
  const events = useNarrativeStore((s) => s.storyEvents);
  const entities = useNarrativeStore((s) => s.storyEntities);
  const aiSummary = useNarrativeStore((s) => s.storyAiSummary);
  const aiFocus = useNarrativeStore((s) => s.storyAiFocus);
  const aiLoading = useNarrativeStore((s) => s.storyAiLoading);
  const storyCameras = useNarrativeStore((s) => s.storyCameras);
  const viewBounds = useNarrativeStore((s) => s.storyViewBounds);
  const cropToView = useNarrativeStore((s) => s.storyCropToView);
  const [promptOpen, setPromptOpen] = useState(false);

  const datasets = useMemo<StoryMapDatasets>(
    () => ({ dataPoints, routes, dwellLocations, actualDevices, itinerarySubmissions, coTravelerResults, signalData }),
    [dataPoints, routes, dwellLocations, actualDevices, itinerarySubmissions, coTravelerResults, signalData],
  );
  // Everything loaded on the map, compacted for the model (cameras passed are
  // merged in by the store from what the map publishes).
  const aiBounds = cropToView ? viewBounds : null;
  const aiContext = useMemo(() => buildStoryAiContext(datasets, entities, aiBounds), [datasets, entities, aiBounds]);
  const aiInventory = describeDatasets(aiContext, events.length, storyCameras, aiBounds);

  const runAi = (focus: string | null, detail?: StoryAiDetail) => {
    const next = detail ?? hud.style.storyAiDetail;
    if (detail) useNarrativeStore.getState().updateHudStyle({ storyAiDetail: detail });
    setPanelOpen('ai', true);
    void generateStoryAiSummary({ focus, context: aiContext, detail: next });
  };
  // From the Story card: show the AI card, and ask what to generate if it is still empty.
  const openAi = () => {
    setPanelOpen('ai', true);
    if (!aiSummary && !aiLoading) setPromptOpen(true);
  };

  if (!storyOpen && !aiOpen) return null;
  return (
    <>
      {storyOpen && <StoryCard currentTime={currentTime} isFullExtent={isFullExtent} onEventSelect={onEventSelect} onOpenAi={openAi} />}
      {aiOpen && <AiCard onOpenPrompt={() => setPromptOpen(true)} onRegenerate={() => runAi(aiFocus)} />}
      <AiSummaryDialog
        open={promptOpen}
        onClose={() => setPromptOpen(false)}
        hud={hud}
        inventory={aiInventory}
        loading={aiLoading}
        initialFocus={aiFocus}
        onGenerate={runAi}
      />
    </>
  );
};

export default PathfinderMapNarrativeOverlay;
